import {
  markierKlasse,
  pulsZeit,
  TASK_STATUS_LABELS,
  type PulsStand,
  type TaskStatus,
} from '@netcon-work/shared'
import { ArrowLeftRight, ExternalLink, Repeat, Sun } from 'lucide-react'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { cn } from '../../lib/cn'
import { heuteSchluessel, useMarkieren, useMarkierungen } from '../../lib/markierung'
import { AufgabenVerweis } from '../AufgabeKontext'
import { Button } from '../ui/primitives'
import type { Block } from '../meintag/tagesplan'
import {
  PulsEnde,
  PulsSteuer,
  zeitText,
  type PulsAufgabe,
  type PulsHandlungen,
} from '../meintag/Puls'
import type { PulsTermin } from '@netcon-work/shared'
import type { Tageslage, TerminLage } from '../../lib/mywork'
import { useMerker } from '../../lib/merker'
import { stundeAus } from '../meintag/tagesplan'
import type { AmTischZeile, BezugAufgabe, TerminAufgaben } from './bezuege'
import { dauerText, uhrAus, type StromEintrag, type StromTermin } from './strom'

/**
 * **Die Bausteine des Tagesstroms** ([Z-24](../../../../../docs/phasen/zusatz-24-my-work.md)).
 * Gerechnet wird in `strom.ts`; hier wird nur gezeichnet.
 */

/** Die Spalte links: Uhrzeit neben jedem Eintrag. */
export function Zeile({
  uhr,
  rot,
  children,
}: {
  uhr: ReactNode
  rot?: boolean
  children: ReactNode
}) {
  return (
    <div className="grid grid-cols-[46px_minmax(0,1fr)] gap-x-3 sm:grid-cols-[52px_minmax(0,1fr)] sm:gap-x-3.5">
      <div
        className={cn(
          'tabular pt-[9px] text-right text-[12px]',
          rot ? 'font-bold text-bad' : 'text-muted',
        )}
      >
        {uhr}
      </div>
      <div className="min-w-0">{children}</div>
    </div>
  )
}

/* ─────────────────────────── Vorbei ─────────────────────────── */

/** Eine Notiz des Tages ohne Termin – aus `notizbuch.mywork.tageslage`. */
export type TagesNotiz = Tageslage['notizenOhneTermin'][number]

/**
 * **Was heute schon war – Termine und Notizen in der Reihenfolge der Uhrzeit** (Nils, 04.11.2026:
 * *„bei my work – da auch die vergangene sachen anzeigen, auch die notizen (wie beim notizen
 * dashboard) das ist ja die kombi daraus."*).
 *
 * Bis dahin stand hier eine graue Zeile mit den Betreffs. Jetzt wie „Heute" im Notizbuch: jeder
 * vergangene Termin als Karte (mit Blatt, Kurzfassung, Protokoll), dazwischen die Notizen ohne
 * Termin. Aufgeklappt ist der Standard; wer es zuklappt, behält das auf diesem Gerät.
 */
export function VorbeiBereich({
  termine,
  notizen,
  karte,
}: {
  termine: readonly StromTermin[]
  notizen: readonly TagesNotiz[]
  /** Die Karte eines vergangenen Termins – gebaut in My Work, dort liegen die Bezüge. */
  karte: (t: StromTermin, von: number, bis: number) => ReactNode
}) {
  const [offen, setOffen] = useMerker('mywork-vorbei', 'auf', ['auf', 'zu'] as const)
  const punkte = [
    ...termine.map((t) => ({
      art: 'termin' as const,
      um: stundeAus(t.beginn) ?? 0,
      bis: stundeAus(t.ende) ?? 0,
      t,
    })),
    ...notizen.map((n) => ({ art: 'notiz' as const, um: ortsStunde(n.datum), n })),
  ].sort((a, b) => a.um - b.um)
  if (punkte.length === 0) return null

  const zahlen = [
    termine.length > 0 && `${termine.length} ${termine.length === 1 ? 'Termin' : 'Termine'}`,
    notizen.length > 0 && `${notizen.length} ${notizen.length === 1 ? 'Notiz' : 'Notizen'}`,
  ].filter(Boolean)

  return (
    <>
      <Zeile uhr={uhrAus(punkte[0]!.um)}>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-xl bg-[#efeee9] px-3.5 py-2 text-[13px] text-ink2">
          <b className="text-[#3d3b37]">Vorbei</b>
          <span>· {zahlen.join(' · ')}</span>
          <button
            type="button"
            onClick={() => setOffen(offen === 'auf' ? 'zu' : 'auf')}
            className="ml-auto cursor-pointer font-semibold text-accent-deep"
            aria-expanded={offen === 'auf'}
          >
            {offen === 'auf' ? 'zuklappen' : 'aufklappen'}
          </button>
        </div>
      </Zeile>
      {offen === 'auf' &&
        punkte.map((p) =>
          p.art === 'termin' ? (
            <div key={`t-${p.t.id}`}>{karte(p.t, p.um, p.bis)}</div>
          ) : (
            <NotizKarte key={`n-${p.n.notizbuchId}-${p.n.notizId}`} n={p.n} um={p.um} />
          ),
        )}
    </>
  )
}

/** Die Ortszeit einer Notiz in Stunden – `datum` trägt (anders als Termine) einen Versatz. */
function ortsStunde(iso: string): number {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? (stundeAus(iso) ?? 0) : d.getHours() + d.getMinutes() / 60
}

const NOTIZ_ZEICHEN: Record<string, string> = {
  besprechung: '👥',
  idee: '💡',
  telefonat: '📞',
  gedanke: '💭',
  begehung: '🏗',
  mitarbeitergespraech: '🤝',
}

/** Eine Notiz im Strom – wie die Notizkarte in „Heute", ein Klick öffnet sie im Notizbuch. */
function NotizKarte({ n, um }: { n: TagesNotiz; um: number }) {
  const zeichen = n.quelle === 'plaud' ? '🎙' : (NOTIZ_ZEICHEN[n.art] ?? '📝')
  return (
    <Zeile uhr={uhrAus(um)}>
      <a
        href={`/notizbuch#notiz-${n.notizbuchId}-${n.notizId}`}
        aria-label={`Notiz ${n.titel || 'Ohne Titel'}`}
        className="flex gap-3 rounded-card border border-line bg-surface px-4 py-2.5 shadow-task hover:border-[#c9c7c0]"
        style={{ borderLeft: '4px solid #9b8ad6' }}
      >
        <span aria-hidden="true" className="text-[17px] leading-6">
          {zeichen}
        </span>
        <div className="min-w-0">
          <b className="block truncate text-[14px]">{n.titel || 'Ohne Titel'}</b>
          {n.text && <p className="line-clamp-2 text-[12.5px] text-ink2">{n.text}</p>}
          {!n.mappe && <div className="text-[11.5px] text-muted">Zettel ohne Mappe</div>}
        </div>
      </a>
    </Zeile>
  )
}

/* ─────────────────────────── Jetzt ─────────────────────────── */

export function JetztLinie({ eintrag }: { eintrag: Extract<StromEintrag, { art: 'jetzt' }> }) {
  const imTermin = !!eintrag.laufend
  return (
    <Zeile
      uhr={
        <span
          className={cn(
            'rounded-full px-1.5 py-px font-bold text-white',
            imTermin ? 'bg-good' : 'bg-pool-sofort',
          )}
        >
          {uhrAus(eintrag.um)}
        </span>
      }
    >
      <div className="flex items-center gap-2 pt-[9px]">
        <div className={cn('h-0.5 flex-1', imTermin ? 'bg-good' : 'bg-pool-sofort')} />
        <span
          className={cn(
            'text-[11.5px] font-bold tracking-[0.03em] uppercase',
            imTermin ? 'text-good-text' : 'text-bad',
          )}
        >
          Jetzt
          {eintrag.laufend
            ? ` · ${eintrag.laufend.betreff} läuft`
            : eintrag.fensterBis !== null
              ? ` · frei bis ${uhrAus(eintrag.fensterBis)}`
              : ''}
        </span>
      </div>
    </Zeile>
  )
}

/* ─────────────────────────── Puls ─────────────────────────── */

/**
 * **Der Puls im Jetzt** – Titel, Uhr, Bogen, Steuerung. Die Steuerung ist dieselbe wie im
 * Circle (`PulsSteuer`), ebenso „Zeit um" (`PulsEnde`) und die Leertaste.
 */
export function PulsKarte({
  stand,
  aufgaben,
  jetzt,
  summen,
  termine,
  h,
  angebot,
  unterBogen,
}: {
  stand: PulsStand | null
  aufgaben: ReadonlyMap<string, PulsAufgabe>
  jetzt: number
  summen: ReadonlyMap<string, number>
  termine: readonly PulsTermin[]
  h: PulsHandlungen
  /** Wenn nichts im Puls ist: was als Nächstes dran wäre. */
  angebot: { id: string; titel: string; grund: string } | null
  /** Unter dem Bogen – der Faden der Aufgabe (Z-24 Stufe 4). */
  unterBogen?: ReactNode
}) {
  const aktiv = stand ? aufgaben.get(stand.aktivId) : undefined
  const ruhend = stand?.ruhendId ? aufgaben.get(stand.ruhendId) : undefined
  const z = stand ? pulsZeit(stand, jetzt) : null
  const markierungen = useMarkierungen()
  const heute = heuteSchluessel()

  /** Zeit um: einmal je Block anhalten und fragen – wie im Circle. */
  const gefragt = useRef(new Set<string>())
  const [endeOffen, setEndeOffen] = useState(false)
  const block = stand ? `${stand.aktivId}|${stand.beginn}|${stand.extraMs}` : null
  const abgelaufen = !!z && z.restMs !== null && z.restMs <= 0 && !z.pausiert
  const pausieren = h.pausieren
  useEffect(() => {
    if (!abgelaufen || !block || gefragt.current.has(block)) return
    gefragt.current.add(block)
    setEndeOffen(true)
    pausieren()
  }, [abgelaufen, block, pausieren])

  /** Leertaste = Pause/Weiter, solange kein Feld den Fokus hat. */
  const umschalten = useRef<() => void>(() => undefined)
  umschalten.current = () => (stand?.pausiertSeit ? h.weiter() : h.pausieren())
  useEffect(() => {
    if (!stand) return
    const taste = (e: KeyboardEvent) => {
      if (e.key !== ' ' || e.metaKey || e.ctrlKey || e.altKey) return
      const ziel = e.target as HTMLElement | null
      if (
        ziel?.closest('input, textarea, select, button, [contenteditable="true"], [role="dialog"]')
      )
        return
      e.preventDefault()
      umschalten.current()
    }
    window.addEventListener('keydown', taste)
    return () => window.removeEventListener('keydown', taste)
  }, [stand])

  if (!stand || !z) {
    return (
      <section
        aria-label="Puls frei"
        className="rounded-card border border-dashed border-[#b9d2f1] bg-[#f7faff] px-4 py-3.5"
      >
        <div className="text-[11px] font-extrabold tracking-[0.08em] text-accent-deep uppercase">
          Puls frei
        </div>
        {angebot ? (
          <div className="mt-1.5 flex flex-wrap items-center gap-3">
            <div className="min-w-0 flex-[1_1_260px]">
              <AufgabenVerweis
                aufgabeId={angebot.id}
                className="block truncate text-[16px] font-semibold hover:text-accent-deep"
              >
                {angebot.titel}
              </AufgabenVerweis>
              <div className="text-[12.5px] text-ink2">{angebot.grund}</div>
            </div>
            <Button variant="primary" onClick={() => h.holen(angebot.id)}>
              ▶ Puls starten
            </Button>
          </div>
        ) : (
          <p className="mt-1 text-[13px] text-ink2">
            Nichts vorgenommen und nichts in Arbeit. Hol dir etwas aus „☀ Vorgenommen" oder einen
            Vorschlag aus einem Fenster.
          </p>
        )}
        {h.hinweis && <Hinweis text={h.hinweis} weg={h.hinweisWeg} />}
      </section>
    )
  }

  const am = markierungen.get(stand.aktivId)
  const unterzeile = z.pausiert
    ? 'pausiert · Leertaste = weiter'
    : stand.uhr === 'termin' && stand.zielAm
      ? `bis ${uhrDerIso(stand.zielAm, stand.extraMs)} · dann ${stand.zielTitel ?? 'Termin'}`
      : stand.uhr === 'offen'
        ? `läuft offen · seit ${uhrDerIso(stand.beginn, 0)}`
        : `${stand.uhr}-min-Block${stand.extraMs ? ` +${Math.round(stand.extraMs / 60_000)}` : ''}`

  return (
    <section
      aria-label="Im Puls"
      className="rounded-card border-[1.5px] border-[#9ed7bf] bg-surface px-4 py-4 shadow-[0_0_0_4px_#e8f6ef,0_6px_22px_rgb(23_22_20/0.06)] sm:px-5"
    >
      <div className="flex flex-wrap items-start gap-4">
        <div className="min-w-0 flex-[1_1_300px]">
          <div className="mb-1 flex flex-wrap items-center gap-2 text-[12px]">
            <span className="text-[11px] font-extrabold tracking-[0.08em] text-good-text uppercase">
              ● Im Puls
            </span>
            {aktiv?.welt && (
              <span className="flex items-center gap-1.5 text-ink2">
                <i
                  className="inline-block size-[7px] rounded-full"
                  style={{ background: aktiv.farbe }}
                />
                {aktiv.welt}
              </span>
            )}
            {am && markierKlasse(am, heute) === 'heute' && (
              <span className="rounded-full bg-sonne-bg px-2 text-sonne">
                ☀ heute{am < heute ? ' · liegengeblieben' : ''}
              </span>
            )}
          </div>
          <h2 className="text-[20px] leading-tight font-semibold tracking-[-0.01em]">
            {aktiv ? (
              <AufgabenVerweis aufgabeId={aktiv.id} className="hover:text-accent-deep">
                {aktiv.title}
              </AufgabenVerweis>
            ) : (
              '…'
            )}
          </h2>
        </div>
        <div className="text-right">
          <div
            className={cn(
              'tabular text-[34px] leading-none font-extrabold tracking-[-0.03em]',
              z.ueberzogen && 'text-warn',
            )}
            aria-live="off"
          >
            {zeitText(z)}
          </div>
          <div className="mt-1 text-[12px] text-ink2">{unterzeile}</div>
        </div>
      </div>
      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-[#eef7f2]" aria-hidden="true">
        <div
          className={cn(
            'h-full rounded-full transition-[width] duration-1000',
            z.endspurt ? 'bg-warn' : 'bg-good',
          )}
          style={{ width: `${Math.round(z.anteil * 100)}%` }}
        />
      </div>

      {unterBogen}

      <PulsSteuer stand={stand} aktiv={aktiv} z={z} h={h} termine={termine} />

      {ruhend && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-line pt-3 text-[13px]">
          <span className="text-muted">ruht:</span>
          <AufgabenVerweis aufgabeId={ruhend.id} className="min-w-0 truncate font-semibold">
            {ruhend.title}
          </AufgabenVerweis>
          <button
            type="button"
            onClick={h.wechseln}
            className="ml-auto flex cursor-pointer items-center gap-1 font-semibold text-accent-deep"
          >
            <ArrowLeftRight size={13} aria-hidden /> tauschen
          </button>
        </div>
      )}
      {h.hinweis && <Hinweis text={h.hinweis} weg={h.hinweisWeg} />}
      {endeOffen && aktiv && (
        <PulsEnde
          aktiv={aktiv}
          heuteMs={summen.get(aktiv.id) ?? 0}
          stand={stand}
          h={h}
          zu={() => setEndeOffen(false)}
        />
      )}
    </section>
  )
}

function Hinweis({ text, weg }: { text: string; weg: () => void }) {
  return (
    <p role="alert" className="mt-2 text-[12.5px] text-bad">
      Nicht gestartet: {text}{' '}
      <button type="button" onClick={weg} className="cursor-pointer underline">
        ok
      </button>
    </p>
  )
}

/** Uhrzeit aus einer ISO-Zeit mit Versatz (Puls-Termine sind echte Zeitpunkte). */
function uhrDerIso(iso: string, extraMs: number): string {
  return new Date(new Date(iso).getTime() + extraMs).toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

/* ─────────────────────────── Termin ─────────────────────────── */

const initialen = (name: string) =>
  name
    .split(/[\s.@_-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((t) => t[0]?.toUpperCase() ?? '')
    .join('')

const PERSONENFARBEN = ['#1c5cab', '#a84a17', '#0f7a55', '#4a3aa7', '#b02f2f', '#5a574f']

export function TerminKarte({
  eintrag,
  jetzt,
  dazu,
  notizbuch,
  festMachen,
  loesen,
  vorbereiten,
  mitschrift,
  abhaken,
  vorbei = false,
}: {
  eintrag: Extract<StromEintrag, { art: 'termin' }>
  /** Stunden seit Mitternacht – für „noch n min". */
  jetzt: number
  /** Work-Aufgaben zu diesem Termin (Z-24 AP 24.17). */
  dazu?: TerminAufgaben
  /** Was das Notizbuch zu diesem Termin weiß (AP 24.18). */
  notizbuch?: TerminLage | undefined
  festMachen?: (taskId: string) => void
  loesen?: (taskId: string) => void
  /** Öffnet das Vorbereitungs-Blatt dieses Termins. */
  vorbereiten?: () => void
  /** Solange er läuft: die Mitschrift (AP 24.23). */
  mitschrift?: ReactNode
  /** Offene Punkte abhaken – nur, wo ich darf (`darfErledigen`); sonst „zur Abnahme". */
  abhaken?: { kann: (taskId: string) => boolean; tun: (taskId: string) => void } | undefined
  /** Schon vorbei: ruhiger gezeichnet, ohne Vorbereitung und „vom letzten Mal". */
  vorbei?: boolean
}) {
  const [vielleichtOffen, setVielleichtOffen] = useState(false)
  const t = eintrag.termin
  const leute = t.teilnehmer ?? []
  const rest = Math.max(0, Math.round((eintrag.bis - jetzt) * 60))
  const anteil = Math.min(1, Math.max(0, (jetzt - eintrag.von) / (eintrag.bis - eintrag.von)))
  const frei = t.zeigtAls === 'free'
  const imGange = eintrag.laufend && !frei
  /** Abhaken gibt es, solange der Termin läuft – dort geht man das Offene durch. */
  const haken = (a: { id: string; status: string | null }) =>
    imGange && abhaken && a.status !== 'erledigt' && abhaken.kann(a.id)
      ? () => abhaken.tun(a.id)
      : undefined
  return (
    <Zeile uhr={uhrAus(eintrag.von)}>
      <article
        aria-label={`Termin ${t.betreff}`}
        className={cn(
          'rounded-card border bg-surface px-4 py-3 shadow-task',
          eintrag.laufend && !frei
            ? 'border-[1.5px] border-[#9ed7bf] shadow-[0_0_0_4px_#e8f6ef]'
            : 'border-line',
          frei && 'opacity-80',
          vorbei && 'bg-[#faf9f6] shadow-none',
        )}
        style={{ borderLeft: `4px solid ${eintrag.laufend && !frei ? '#1baf7a' : '#c9c7c0'}` }}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-[1_1_240px]">
            {eintrag.laufend && !frei && (
              <div className="text-[11px] font-extrabold tracking-[0.08em] text-good-text uppercase">
                ● läuft · noch {rest} min
              </div>
            )}
            <h3 className="text-[15px] font-semibold">{t.betreff}</h3>
            <div className="flex flex-wrap items-center gap-x-2 text-[12.5px] text-ink2">
              <span className="tabular">
                {uhrAus(eintrag.von)}–{uhrAus(eintrag.bis)}
              </span>
              {t.ort && <span className="max-w-[220px] truncate">· {t.ort}</span>}
              {t.serie && (
                <span className="flex items-center gap-1" title="Gehört zu einer Serie">
                  · <Repeat size={11} aria-hidden /> Serie
                </span>
              )}
              {frei && <span>· als frei eingetragen</span>}
            </div>
          </div>
          {leute.length > 0 && (
            <div
              className="flex items-center"
              aria-label={leute.join(', ')}
              title={leute.join(', ')}
            >
              {leute.slice(0, 3).map((n, i) => (
                <span
                  key={`${n}-${i}`}
                  aria-hidden="true"
                  className={cn(
                    'flex size-[26px] items-center justify-center rounded-full border-2 border-surface text-[10.5px] font-bold text-white',
                    i > 0 && '-ml-[7px]',
                  )}
                  style={{ background: PERSONENFARBEN[i % PERSONENFARBEN.length] }}
                >
                  {initialen(n)}
                </span>
              ))}
              {leute.length > 3 && (
                <span
                  aria-hidden="true"
                  className="-ml-[7px] flex size-[26px] items-center justify-center rounded-full border-2 border-surface bg-[#efeee9] text-[10.5px] font-bold text-ink2"
                >
                  +{leute.length - 3}
                </span>
              )}
            </div>
          )}
        </div>
        {notizbuch && (
          <NotizbuchZeile
            nb={notizbuch}
            laufend={eintrag.laufend}
            vorbei={vorbei}
            vorbereiten={vorbereiten}
            ohneBlattLink={imGange && !!mitschrift}
            haken={haken}
          />
        )}
        {eintrag.laufend && !frei && (
          <div className="mt-2 h-1 overflow-hidden rounded-full bg-[#e3f1ea]" aria-hidden="true">
            <div className="h-full bg-good" style={{ width: `${Math.round(anteil * 100)}%` }} />
          </div>
        )}
        {imGange && mitschrift}
        {dazu && (dazu.fest.length > 0 || (!vorbei && dazu.offenLetztesMal.length > 0)) && (
          <div className="mt-2.5 flex flex-col gap-1.5 rounded-[10px] bg-[#faf9f6] px-3 py-2 text-[13px]">
            {!vorbei && dazu.offenLetztesMal.length > 0 && (
              <>
                <div className="text-[10.5px] font-bold tracking-[0.08em] text-ink2 uppercase">
                  Offen vom letzten Mal
                </div>
                {dazu.offenLetztesMal.map((a) => (
                  <AufgabeZeile key={a.id} a={a} abhaken={haken(a)} />
                ))}
              </>
            )}
            {dazu.fest.length > 0 && (
              <>
                <div className="text-[10.5px] font-bold tracking-[0.08em] text-ink2 uppercase">
                  Work-Aufgaben dazu
                </div>
                {dazu.fest.map((a) => (
                  <AufgabeZeile
                    key={a.id}
                    a={a}
                    abhaken={haken(a)}
                    aktion={loesen ? { text: 'lösen', tun: () => loesen(a.id) } : undefined}
                  />
                ))}
              </>
            )}
          </div>
        )}
        {!vorbei && dazu && dazu.zwischenEuch.length > 0 && (
          <div className="mt-2 text-[13px]">
            <button
              type="button"
              aria-expanded={vielleichtOffen}
              onClick={() => setVielleichtOffen((x) => !x)}
              className="cursor-pointer text-[12.5px] font-semibold text-accent-deep"
            >
              {vielleichtOffen ? '▾' : '▸'} {dazu.zwischenEuch.length} passen vielleicht – zwischen
              euch offen
            </button>
            {vielleichtOffen && (
              <div className="mt-1.5 flex flex-col gap-1.5">
                {dazu.zwischenEuch.map(({ aufgabe: a, mit }) => (
                  <AufgabeZeile
                    key={a.id}
                    a={a}
                    zusatz={`mit ${mit.split(' ')[0]}`}
                    aktion={
                      festMachen ? { text: 'fest machen', tun: () => festMachen(a.id) } : undefined
                    }
                  />
                ))}
              </div>
            )}
          </div>
        )}
        {t.webLink && (
          <a
            href={t.webLink}
            target="_blank"
            rel="noreferrer"
            className="mt-2 inline-flex items-center gap-1 text-[12.5px] font-semibold text-accent-deep"
          >
            in Outlook <ExternalLink size={11} aria-hidden />
          </a>
        )}
      </article>
    </Zeile>
  )
}

/** Chips und offene Punkte aus dem Notizbuch an der Termin-Karte. */
function NotizbuchZeile({
  nb,
  laufend,
  vorbei = false,
  vorbereiten,
  ohneBlattLink = false,
  haken,
}: {
  nb: TerminLage
  laufend: boolean
  vorbei?: boolean
  vorbereiten?: (() => void) | undefined
  /** Im laufenden Termin steht „Großes Blatt ↗" an der Mitschrift. */
  ohneBlattLink?: boolean
  haken?: (a: { id: string; status: string | null }) => (() => void) | undefined
}) {
  const chip = 'rounded-full px-2 py-px text-[12px]'
  const blattAdresse = nb.blatt
    ? `/notizbuch#notiz-${nb.blatt.notizbuchId}-${nb.blatt.notizId}`
    : null
  return (
    <div className="mt-2 flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-1.5">
        {nb.reihe > 0 &&
          !vorbei &&
          (nb.vorbereitet ? (
            <button
              type="button"
              onClick={vorbereiten}
              className={cn(chip, 'cursor-pointer bg-good-bg text-good-text')}
            >
              ✓ vorbereitet – ansehen
            </button>
          ) : (
            <span className={cn(chip, 'bg-sonne-bg text-sonne')}>noch nicht vorbereitet</span>
          ))}
        {nb.reihe > 0 && (
          <span className={cn(chip, 'bg-[#f3f2ef] text-ink2')}>
            ↻ {nb.reihe + 1}. Runde
            {nb.letzteRunde
              ? ` · zuletzt ${new Date(`${nb.letzteRunde}T12:00:00`).toLocaleDateString('de-DE', { day: 'numeric', month: 'numeric' })}`
              : ''}
          </span>
        )}
        {nb.aufnahme && <span className={cn(chip, 'bg-[#f3f2ef] text-ink2')}>Aufnahme</span>}
        {nb.protokoll && <span className={cn(chip, 'bg-good-bg text-good-text')}>✓ Protokoll</span>}
        {nb.aufgabenErkannt > 0 && (
          <span className={cn(chip, 'bg-[#fdf3dc] text-[#8a6100]')}>
            {nb.aufgabenErkannt} Aufgaben erkannt
          </span>
        )}
        {blattAdresse
          ? !ohneBlattLink && (
              <a href={blattAdresse} className="text-[12.5px] font-semibold text-accent-deep">
                {laufend ? 'Mitschrift öffnen' : 'Blatt öffnen'}
              </a>
            )
          : nb.reihe > 0 &&
            !vorbei &&
            !nb.vorbereitet && (
              <button
                type="button"
                onClick={vorbereiten}
                className="cursor-pointer text-[12.5px] font-semibold text-accent-deep"
              >
                ✦ Vorbereiten
              </button>
            )}
      </div>
      {nb.kurzfassung && <p className="text-[12.5px] text-ink2">{nb.kurzfassung}</p>}
      {!vorbei && nb.offenLetztesMal.length > 0 && (
        <div className="flex flex-col gap-1 rounded-[10px] bg-[#faf9f6] px-3 py-2 text-[13px]">
          <div className="text-[10.5px] font-bold tracking-[0.08em] text-ink2 uppercase">
            Aus der letzten Runde
          </div>
          {nb.offenLetztesMal.map((p, i) => (
            <div key={`${p.text}-${i}`} className="flex flex-wrap items-center gap-x-2">
              {p.aufgabe && haken?.(p.aufgabe) && (
                <HakenKnopf titel={p.text} tun={haken(p.aufgabe)!} />
              )}
              <span className="min-w-0 flex-[1_1_180px]">
                {p.aufgabe ? (
                  <AufgabenVerweis aufgabeId={p.aufgabe.id} className="hover:text-accent-deep">
                    {p.text}
                  </AufgabenVerweis>
                ) : (
                  p.text
                )}
              </span>
              <span
                className={cn(
                  'rounded-full px-2 py-px text-[11.5px]',
                  !p.aufgabe
                    ? 'bg-[#f3f2ef] text-muted'
                    : p.aufgabe.status === 'erledigt'
                      ? 'bg-good-bg text-good-text'
                      : 'bg-accent-soft text-accent-deep',
                )}
              >
                {p.aufgabe
                  ? (TASK_STATUS_LABELS[p.aufgabe.status as TaskStatus] ?? p.aufgabe.status ?? '–')
                  : 'noch keine Aufgabe'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/** Ein Kreis zum Abhaken – Erledigt oder Zur Abnahme, je nachdem, was ich darf. */
function HakenKnopf({
  titel,
  tun,
  gross = false,
}: {
  titel: string
  tun: () => void
  /** In den Fenstern: am Telefon eine Tippfläche von 40 px. */
  gross?: boolean
}) {
  return (
    <button
      type="button"
      onClick={tun}
      aria-label={`Abhaken: ${titel}`}
      title="abhaken"
      className={cn(
        'flex shrink-0 cursor-pointer items-center justify-center rounded-full border-[1.5px] border-[#b7c9bf] text-transparent hover:border-good hover:text-good',
        gross
          ? 'size-[24px] text-[12px] max-sm:size-10 max-sm:text-[15px]'
          : 'size-[22px] text-[11px]',
      )}
    >
      ✓
    </button>
  )
}

function AufgabeZeile({
  a,
  zusatz,
  aktion,
  abhaken,
}: {
  a: BezugAufgabe
  zusatz?: string
  aktion?: { text: string; tun: () => void } | undefined
  abhaken?: (() => void) | undefined
}) {
  const label = TASK_STATUS_LABELS[a.status as TaskStatus] ?? a.status
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
      {abhaken && <HakenKnopf titel={a.title} tun={abhaken} />}
      <AufgabenVerweis
        aufgabeId={a.id}
        className="min-w-0 flex-[1_1_180px] truncate hover:text-accent-deep"
      >
        {a.title}
      </AufgabenVerweis>
      <span
        className={cn(
          'rounded-full px-2 py-px text-[11.5px]',
          a.status === 'in_arbeit'
            ? 'bg-accent-soft text-accent-deep'
            : a.status === 'erledigt'
              ? 'bg-good-bg text-good-text'
              : a.status === 'warten'
                ? 'bg-[#fdf1ea] text-[#a84a17]'
                : 'bg-[#f3f2ef] text-ink2',
        )}
      >
        {label}
        {a.assigneeName && a.status === 'in_arbeit' ? ` · ${a.assigneeName.split(' ')[0]}` : ''}
      </span>
      {zusatz && <span className="text-[12px] text-muted">{zusatz}</span>}
      {aktion && (
        <button
          type="button"
          onClick={aktion.tun}
          className="cursor-pointer text-[12px] font-semibold text-accent-deep"
        >
          {aktion.text}
        </button>
      )}
    </div>
  )
}

/* ─────────────────────────── Am Tisch ─────────────────────────── */

/** **Am Tisch** (AP 24.19) – solange ein Termin läuft: wer dabei ist und was mit ihm offen ist. */
export function AmTisch({ termin, zeilen }: { termin: string; zeilen: readonly AmTischZeile[] }) {
  if (zeilen.length === 0) return null
  return (
    <section
      aria-label="Am Tisch"
      className="rounded-card border border-line bg-surface px-3.5 py-3 shadow-task"
    >
      <h2 className="mb-2 text-[10.5px] font-bold tracking-[0.08em] text-ink2 uppercase">
        Am Tisch · {termin}
      </h2>
      <ul className="flex flex-col gap-2.5">
        {zeilen.map((z, i) => (
          <li key={`${z.name}-${i}`} className="flex gap-2.5 text-[13px]">
            <span
              aria-hidden="true"
              className="flex size-[28px] shrink-0 items-center justify-center rounded-full text-[10.5px] font-bold text-white"
              style={{
                background: z.person ? PERSONENFARBEN[i % PERSONENFARBEN.length] : '#a8a6a0',
              }}
            >
              {initialen(z.name)}
            </span>
            <div className="min-w-0 flex-1">
              <div>
                <b>{z.name}</b>
                {!z.person && <span className="text-muted"> · extern</span>}
              </div>
              {z.person && z.inArbeit.length === 0 && z.zurAbnahme.length === 0 && (
                <div className="text-[12px] text-muted">nichts in Arbeit</div>
              )}
              {z.inArbeit.map((a) => (
                <div key={a.id} className="truncate text-[12.5px] text-ink2">
                  in Arbeit:{' '}
                  <AufgabenVerweis aufgabeId={a.id} className="hover:text-accent-deep">
                    {a.title}
                  </AufgabenVerweis>
                </div>
              ))}
              {z.zurAbnahme.map((a) => (
                <div key={a.id} className="truncate text-[12.5px] text-[#8a5300]">
                  wartet auf deine Abnahme:{' '}
                  <AufgabenVerweis aufgabeId={a.id} className="hover:text-accent-deep">
                    {a.title}
                  </AufgabenVerweis>
                </div>
              ))}
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}

/* ─────────────────────────── Fenster ─────────────────────────── */

export function FensterKarte({
  eintrag,
  jetzt,
  imPuls,
  holen,
  nurFuer,
  erledigen,
}: {
  eintrag: Extract<StromEintrag, { art: 'fenster' }>
  jetzt: number
  imPuls: (id: string) => boolean
  holen: (id: string) => void
  /**
   * **Abhaken direkt im Fenster** (Nils, 24.10.2026, am Telefon: *„Hier fehlt noch, dass ich die
   * starten und erledigt setzen kann"*) – Erledigt oder Zur Abnahme (`darfErledigen`).
   */
  erledigen?: ((id: string) => void) | undefined
  /** Gibt es heute nur die Arbeitszeit einer Welt (Samstag: PRIVAT), steht ihr Name hier. */
  nurFuer?: string | null
}) {
  const markieren = useMarkieren()
  const heute = heuteSchluessel()
  const von = eintrag.jetztDrin ? Math.max(eintrag.von, jetzt) : eintrag.von
  const lang = eintrag.bis - von >= 1.5
  return (
    <Zeile uhr={eintrag.jetztDrin ? '' : uhrAus(eintrag.von)}>
      <section
        aria-label={`Fenster ${uhrAus(von)} bis ${uhrAus(eintrag.bis)}`}
        className={cn(
          'flex flex-col gap-2 rounded-xl border-[1.5px] border-dashed px-3.5 py-2.5',
          lang ? 'border-[#b9d2f1] bg-[#f7faff]' : 'border-[#cfd9e6] bg-[#fbfcfe]',
        )}
      >
        <div className="flex flex-wrap items-baseline gap-x-2">
          <b className="text-[13.5px]">Fenster · {dauerText(eintrag.bis - von)}</b>
          <span className="tabular text-[12.5px] text-ink2">
            {uhrAus(von)}–{uhrAus(eintrag.bis)}
            {nurFuer
              ? ` · nur ${nurFuer}`
              : lang
                ? ' · Zeit für Tiefes'
                : eintrag.bis - von <= 1
                  ? ' · gut für Kleines'
                  : ''}
          </span>
        </div>
        {eintrag.bloecke.length === 0 ? (
          <p className="text-[12.5px] text-ink2">Nichts gelegt – frei.</p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {eintrag.bloecke.map((b, i) => (
              <BlockZeile
                key={`${b.aufgabe?.id ?? b.titel}-${i}`}
                b={b}
                imPuls={b.aufgabe ? imPuls(b.aufgabe.id) : false}
                holen={holen}
                erledigen={erledigen}
                markieren={(id) => markieren.mutate({ taskIds: [id], am: heute })}
              />
            ))}
          </ul>
        )}
        {eintrag.bloecke.length > 0 && eintrag.frei >= 0.25 && (
          <div className="text-[12px] text-ink2">Rest {dauerText(eintrag.frei)} frei</div>
        )}
      </section>
    </Zeile>
  )
}

const ART_TEXT: Partial<Record<Block['art'], string>> = {
  aufgabe: 'in Arbeit',
  markiert: '☀ vorgenommen',
  fest: 'fest',
}

function BlockZeile({
  b,
  imPuls,
  holen,
  markieren,
  erledigen,
}: {
  b: Block
  imPuls: boolean
  holen: (id: string) => void
  markieren: (id: string) => void
  erledigen?: ((id: string) => void) | undefined
}) {
  const vorschlag = b.art === 'vorschlag'
  const id = b.aufgabe?.id
  /** Am Telefon 40 px hoch – die Zeilen sind dort der Hauptweg (Z-10.5). */
  const knopf =
    'flex h-[28px] shrink-0 cursor-pointer items-center justify-center rounded-lg px-2 text-[12.5px] font-semibold max-sm:h-10 max-sm:min-w-10 max-sm:text-[15px]'
  /**
   * **Eine Zeile: ○ · Art und Dauer über dem Titel · Knöpfe rechts** (24.10.2026, am Telefon
   * gesehen: mit ✓ und ▶ brach die alte Zeile auf drei Zeilen um).
   */
  return (
    <li
      className={cn(
        'flex items-center gap-2.5 rounded-[10px] px-3 py-[7px] text-[13px]',
        vorschlag
          ? 'border border-dashed border-[#c3dcf5] bg-surface text-[#2a5f8f]'
          : 'border border-line bg-surface',
      )}
    >
      {id && !vorschlag && erledigen && (
        <HakenKnopf titel={b.titel} tun={() => erledigen(id)} gross />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 overflow-hidden text-[10.5px] font-bold tracking-[0.04em] whitespace-nowrap uppercase">
          <span
            className={cn(
              vorschlag
                ? 'text-[#2a5f8f]'
                : b.art === 'markiert'
                  ? 'text-sonne'
                  : 'text-accent-deep',
            )}
          >
            {vorschlag ? (
              'Vorschlag'
            ) : imPuls ? (
              '● im Puls'
            ) : b.art === 'markiert' ? (
              /** Am Telefon nur ☀ – sonst schnitt die Dauer ab (24.10.2026). */
              <>
                ☀<span className="max-sm:hidden"> vorgenommen</span>
              </>
            ) : (
              (ART_TEXT[b.art] ?? '')
            )}
          </span>
          <span className="tabular shrink-0 font-normal tracking-normal text-ink2 normal-case">
            · {dauerText(b.bis - b.von)}
            {b.teil ? ` · Teil ${b.teil.nr}/${b.teil.von}` : ''}
          </span>
        </div>
        <div className="truncate max-sm:line-clamp-2 max-sm:whitespace-normal">
          {id ? (
            <AufgabenVerweis
              aufgabeId={id}
              className={cn('font-semibold hover:text-accent-deep', vorschlag && 'text-[#1f4f7a]')}
            >
              {b.titel}
            </AufgabenVerweis>
          ) : (
            b.titel
          )}
        </div>
      </div>
      {id && vorschlag && (
        <button
          type="button"
          onClick={() => markieren(id)}
          title="Für heute vornehmen"
          aria-label={`Für heute vornehmen: ${b.titel}`}
          className={cn(knopf, 'border border-[#c3dcf5] text-accent-deep')}
        >
          <Sun size={13} aria-hidden />
        </button>
      )}
      {/* ▶ auch vor Beginn des Fensters – wer früher anfängt, soll es dürfen. */}
      {id && !imPuls && (
        <button
          type="button"
          onClick={() => holen(id)}
          aria-label={`In den Puls: ${b.titel}`}
          title="Puls starten"
          className={cn(knopf, 'border border-[#bcd5f3] bg-accent-soft text-accent-deep')}
        >
          ▶
        </button>
      )}
    </li>
  )
}

/* ─────────────────────────── Ende ─────────────────────────── */

export function EndeZeile({ um, abschliessen }: { um: number; abschliessen?: () => void }) {
  return (
    <Zeile uhr={uhrAus(um)}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-xl bg-[#f1effb] px-3.5 py-2 text-[13px] text-[#4a3aa7]">
        <b>Arbeitsende</b>
        <span>· Unfertiges verteilen, offene Enden schließen, Blick auf morgen</span>
        {abschliessen && (
          <button
            type="button"
            onClick={abschliessen}
            className="ml-auto cursor-pointer rounded-lg bg-[#4a3aa7] px-2.5 py-1 text-[12.5px] font-semibold text-white"
          >
            Tag abschließen
          </button>
        )}
      </div>
    </Zeile>
  )
}
